/**
 * Tipo de artículo derivado del prefijo de su código (itemid / articulo).
 *
 * Modo estándar (no AVP):  Q → químicas · P → producto · X → semiacabado
 *   (por la primera letra del código).
 * Modo AVP:                QARF → químicas · ARF → productos
 *   (por el prefijo del código; usado cuando el rol o la empresa es AVP).
 *
 * Usado por el filtro multi-selección de Vista Avanzada (Gantt), Producción
 * Diaria y Plan de Producción.
 */
export type ItemTypeStd = 'Q' | 'P' | 'X'
export type ItemTypeAvp = 'QARF' | 'ARF'
export type ItemType = ItemTypeStd | ItemTypeAvp

/** Opciones del filtro según el modo. */
export function itemTypesFor(avp: boolean): ItemType[] {
  return avp ? ['QARF', 'ARF'] : ['Q', 'P', 'X']
}

/** Tipo del código según el modo. null si no encaja en ninguna opción del modo. */
export function itemTypeOf(code: string | null | undefined, avp = false): ItemType | null {
  if (!code) return null
  const c = code.trim().toUpperCase()
  if (avp) {
    if (c.startsWith('QARF')) return 'QARF'
    if (c.startsWith('ARF')) return 'ARF'
    return null
  }
  // Producto AVP (ARF…) en vista estándar: cuenta como Producto junto a los P…
  // (así AVI —empieza por P— y AVP —empieza por ARF— caen ambos en «Producto»).
  if (c.startsWith('ARF')) return 'P'
  const first = c.charAt(0)
  return first === 'Q' || first === 'P' || first === 'X' ? (first as ItemTypeStd) : null
}

/**
 * ¿El código pasa el filtro? Conjunto vacío ⇒ true (≡ «Todos», sin filtro).
 * Con tipos concretos, solo pasan los códigos cuyo tipo (en el modo dado) está
 * seleccionado; los códigos fuera de las opciones del modo nunca pasan.
 */
export function matchesItemType(
  code: string | null | undefined,
  selected: Set<ItemType>,
  avp = false,
): boolean {
  if (selected.size === 0) return true
  const ty = itemTypeOf(code, avp)
  return ty !== null && selected.has(ty)
}
